// @flow
import React from "react"
import { FormattedMessage } from "react-intl"
import { connect } from "react-redux"
import {
  currentScaleFactor,
  getContainerCoordinate
} from "../FixedResolutionContainer"
import { TweenMax, Cubic } from "gsap"
import SVGLoader from "../../UI/SVGLoader"
import BackgroundContainer from "../../UI/BackgroundContainer"
import TitleIcon from "../Shared/TitleIcon"
import {
  Intro,
  IntroDescription,
  IntroTitle,
  IntroSecondary,
  TimelapseImage,
  TimelapseImageSet,
  NoBurn,
  NoBurnIcon,
  NoBurnDescription,
  NoBurnTitle
} from "./UI"
import Slider from "./Slider"
import images from "./images"
import iconFire from "../Icons/iconFire.svg"
import iconNoBurn from "./noBurningPendant.png"
import background from "./background.jpg"

import type { CoordinateConversion } from "../FixedResolutionContainer"

type SectionName = "blank" | "intro" | "noburn"

type State = {
  currentSection: SectionName,
  currentImage: number
}

type Props = {
  currentScaleFactor: number,
  getContainerCoordinate: CoordinateConversion
}

class WoodburningView extends React.Component<Props, State> {
  introText: Array<HTMLElement>
  noBurnText: Array<HTMLElement>
  introAnimations: any[]
  noBurnAnimations: any[]

  constructor(props: Props) {
    super(props)
    this.introText = []
    this.noBurnText = []
    this.introAnimations = []
    this.noBurnAnimations = []
    this.state = { currentSection: "blank", currentImage: -1 }
  }

  componentDidMount() {
    const introText = this.introText.filter(el => el !== null)
    const noBurnText = this.noBurnText.filter(el => el !== null)
    TweenMax.set(introText, { y: "-40", opacity: 0 })
    TweenMax.set(noBurnText, { y: "-40", opacity: 0 })
    this.showIntroText()
  }

  animateFrames(frame: number) {
    this.setState({ currentImage: frame })
  }

  interruptAnimations = (animations: TweenMax[]) => {
    for (let animation of animations) {
      animation.kill()
    }
  }

  showIntroText = () => {
    const introText = this.introText.filter(el => el !== null)
    const { currentSection } = this.state
    if (currentSection !== "intro") {
      this.interruptAnimations(this.introAnimations)
      this.introAnimations = TweenMax.staggerTo(
        introText,
        0.5,
        { y: "0", opacity: 1, ease: Cubic.easeOut },
        0.2
      )
      this.setState({ currentSection: "intro" })
    }
  }

  showNoBurnText = () => {
    const noBurnText = this.noBurnText.filter(el => el !== null)
    const { currentSection } = this.state
    if (currentSection !== "noburn") {
      this.interruptAnimations(this.noBurnAnimations)
      this.noBurnAnimations = TweenMax.staggerTo(
        noBurnText,
        0.5,
        { y: "0", opacity: 1, ease: Cubic.easeOut },
        0.2
      )
      this.setState({ currentSection: "noburn" })
    }
  }

  hideIntroText = () => {
    const introText = this.introText.filter(el => el !== null)
    const { currentSection } = this.state
    if (currentSection === "intro") {
      this.interruptAnimations(this.introAnimations)
      this.introAnimations = TweenMax.staggerTo(
        introText,
        0.2,
        { y: "40", opacity: 0, ease: Cubic.easeOut },
        0.1
      )
      this.setState({ currentSection: "blank" })
    }
  }

  hideNoBurnText = () => {
    const noBurnText = this.noBurnText.filter(el => el !== null)
    const { currentSection } = this.state
    if (currentSection === "noburn") {
      this.interruptAnimations(this.noBurnAnimations)
      this.noBurnAnimations = TweenMax.staggerTo(
        noBurnText,
        0.2,
        { y: "+40", opacity: 0, ease: Cubic.easeOut },
        0.1
      )
      this.setState({ currentSection: "blank" })
    }
  }

  handleSlider = (percent: number) => {
    const frame = Math.round(percent * 33) - 1
    this.animateFrames(frame)

    if (percent >= 0.05) {
      this.hideIntroText()
    } else {
      this.showIntroText()
    }
    if (percent >= 0.8) {
      this.showNoBurnText()
    } else {
      this.hideNoBurnText()
    }
  }

  render() {
    const { currentImage } = this.state
    const indicies = [currentImage - 1, currentImage, currentImage + 1].filter(
      index => index < images.length
    )
    return (
      <BackgroundContainer imgSrc={background} flex={true}>
        <Intro>
          <TitleIcon innerRef={el => this.introText.push(el)}>
            <SVGLoader path={iconFire} height="100%" width="100%" fill="#fff" />
          </TitleIcon>
          <div>
            <IntroTitle innerRef={el => this.introText.push(el)}>
              <FormattedMessage id="kiosk.woodburning.title" />
            </IntroTitle>
            <IntroDescription innerRef={el => this.introText.push(el)}>
              <FormattedMessage id="kiosk.woodburning.intro" />
            </IntroDescription>
            <IntroSecondary innerRef={el => this.introText.push(el)}>
              <FormattedMessage id="kiosk.woodburning.secondary" />
            </IntroSecondary>
          </div>
        </Intro>
        <NoBurn>
          <NoBurnIcon
            src={iconNoBurn}
            innerRef={el => this.noBurnText.push(el)}
          />
          <div>
            <NoBurnTitle innerRef={el => this.noBurnText.push(el)}>
              <FormattedMessage id="kiosk.woodburning.noburn.title" />
              <br />
              <FormattedMessage id="kiosk.woodburning.noburn.subtitle" />
            </NoBurnTitle>
            <NoBurnDescription innerRef={el => this.noBurnText.push(el)}>
              <FormattedMessage id="kiosk.woodburning.noburn.description1" />
            </NoBurnDescription>
            <NoBurnDescription innerRef={el => this.noBurnText.push(el)}>
              <FormattedMessage id="kiosk.woodburning.noburn.description2" />
            </NoBurnDescription>
          </div>
        </NoBurn>
        <Slider
          delay={1}
          onChange={this.handleSlider}
          scale={this.props.currentScaleFactor}
          conversion={this.props.getContainerCoordinate}
        />
        <TimelapseImageSet visible={currentImage > -1}>
          {indicies.map((index, i) => (
            <TimelapseImage
              visible={currentImage === index}
              src={images[index]}
              key={`timelapseImage${i}`}
            />
          ))}
        </TimelapseImageSet>
      </BackgroundContainer>
    )
  }
}

export default connect(
  state => ({
    currentScaleFactor: currentScaleFactor(state),
    getContainerCoordinate: getContainerCoordinate(state)
  }),
  null
)(WoodburningView)



// WEBPACK FOOTER //
// ./src/Kiosk/WoodburningView/index.js